import mqttService from '../services/MqttService.js'
import { GPIO_ENTITY_ID_MAX_LENGTH, GPIO_MAX_BINDINGS } from './GpioBindingModel.js'
import { gpioDescriptorFromConfig } from './gpioPlateConfig.js'

export const GPIO_METADATA_TOPIC_SUFFIX = '/state/gpio/entities'
export const GPIO_METADATA_CACHE_KEY = 'haspdesigner_gpio_metadata_cache_v1'
export const GPIO_METADATA_VERSION = 1
export const GPIO_SUPPORTED_DOMAINS = Object.freeze([
  'light',
  'switch',
  'binary_sensor',
  'sensor',
  'number',
])

const DOMAIN_SET = new Set(GPIO_SUPPORTED_DOMAINS)
const MAX_UINT32 = 0xffffffff
let adapterSequence = 0

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function clone(value) {
  if (Array.isArray(value)) return value.map(clone)
  if (isPlainObject(value)) return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, clone(item)]))
  return value
}

function asStorage(storage) {
  if (storage && typeof storage.getItem === 'function' && typeof storage.setItem === 'function') return storage
  if (typeof localStorage !== 'undefined') return localStorage
  return null
}

function stablePlateId(plate) {
  if (isPlainObject(plate)) {
    if (plate.id !== undefined && plate.id !== null && plate.id !== '') return String(plate.id)
    if (plate.uuid) return String(plate.uuid)
    if (plate.name) return `name:${plate.name}`
  }
  if (plate !== undefined && plate !== null && plate !== '') return String(plate)
  return ''
}

function plateName(plate, fallback = '') {
  if (isPlainObject(plate)) return String(plate.name ?? fallback ?? '')
  return String(fallback || plate || '')
}

function topicForPlate(plate) {
  const name = plateName(plate)
  return name ? `hasp/${name}${GPIO_METADATA_TOPIC_SUFFIX}` : ''
}

function descriptorFingerprint(descriptor) {
  return JSON.stringify(descriptor)
}

function plateConfigFingerprint(plate) {
  return JSON.stringify(isPlainObject(plate) ? plate.gpio_config ?? null : null)
}

function diagnostic(severity, code, path, message) {
  return { severity, code, path, message }
}

function validEntityId(value) {
  return typeof value === 'string'
    && value.length > 0
    && value.length <= GPIO_ENTITY_ID_MAX_LENGTH
    && /^[A-Za-z_][A-Za-z0-9_-]*$/.test(value)
}

function validUInt32(value) {
  return Number.isInteger(value) && value >= 0 && value <= MAX_UINT32
}

function validateProperty(property, path, diagnostics) {
  if (!isPlainObject(property)) {
    diagnostics.push(diagnostic('error', 'property-not-object', path, 'GPIO metadata properties must be objects.'))
    return
  }
  if (property.type !== undefined && typeof property.type !== 'string') {
    diagnostics.push(diagnostic('error', 'invalid-property-type', `${path}.type`, 'GPIO metadata property type must be a string.'))
  }
  if (property.writable !== undefined && typeof property.writable !== 'boolean') {
    diagnostics.push(diagnostic('error', 'invalid-writable', `${path}.writable`, 'GPIO metadata writable must be boolean.'))
  }
  for (const key of ['min', 'max']) {
    if (property[key] !== undefined && (!Number.isFinite(property[key]) || typeof property[key] === 'boolean')) {
      diagnostics.push(diagnostic('error', `invalid-property-${key}`, `${path}.${key}`, `GPIO metadata ${key} must be numeric.`))
    }
  }
  if (property.length !== undefined && !Number.isInteger(property.length)) {
    diagnostics.push(diagnostic('error', 'invalid-property-length', `${path}.length`, 'GPIO metadata length must be an integer.'))
  }
  if (property.unit !== undefined && typeof property.unit !== 'string') {
    diagnostics.push(diagnostic('error', 'invalid-property-unit', `${path}.unit`, 'GPIO metadata unit must be a string.'))
  }
  if (Number.isFinite(property.min) && Number.isFinite(property.max) && property.min > property.max) {
    diagnostics.push(diagnostic('error', 'invalid-property-range', path, 'GPIO metadata min must not exceed max.'))
  }
}

function validateEntity(entity, index, diagnostics) {
  const path = `entities[${index}]`
  if (!isPlainObject(entity)) {
    diagnostics.push(diagnostic('error', 'entity-not-object', path, 'GPIO metadata entities must be objects.'))
    return
  }
  if (!validEntityId(entity.id)) {
    diagnostics.push(diagnostic('error', 'invalid-entity-id', `${path}.id`, `Entity ID must match firmware format and be at most ${GPIO_ENTITY_ID_MAX_LENGTH} characters.`))
  }
  if (entity.name !== undefined && typeof entity.name !== 'string') {
    diagnostics.push(diagnostic('error', 'invalid-entity-name', `${path}.name`, 'Entity name must be a string when present.'))
  }
  if (typeof entity.domain !== 'string' || !DOMAIN_SET.has(entity.domain)) {
    diagnostics.push(diagnostic('error', 'unsupported-domain', `${path}.domain`, `Unsupported GPIO entity domain '${entity.domain ?? ''}'.`))
  }
  if (entity.capabilities !== undefined) {
    if (!Array.isArray(entity.capabilities)) {
      diagnostics.push(diagnostic('error', 'invalid-capabilities', `${path}.capabilities`, 'Entity capabilities must be an array.'))
    } else {
      const seen = new Set()
      for (const capability of entity.capabilities) {
        if (typeof capability !== 'string') {
          diagnostics.push(diagnostic('error', 'invalid-capability', `${path}.capabilities`, 'Entity capabilities must contain strings.'))
        } else if (seen.has(capability)) {
          diagnostics.push(diagnostic('error', 'duplicate-capability', `${path}.capabilities`, `Capability '${capability}' is duplicated.`))
        } else {
          seen.add(capability)
          if (!['state', 'brightness', 'rgb_color', 'value'].includes(capability)) {
            diagnostics.push(diagnostic('warning', 'unknown-capability', `${path}.capabilities`, `Unknown GPIO capability '${capability}' was preserved.`))
          }
        }
      }
    }
  }
  if (entity.properties !== undefined) {
    if (!isPlainObject(entity.properties)) {
      diagnostics.push(diagnostic('error', 'invalid-properties', `${path}.properties`, 'Entity properties must be an object.'))
    } else {
      for (const [propertyName, property] of Object.entries(entity.properties)) {
        validateProperty(property, `${path}.properties.${propertyName}`, diagnostics)
      }
    }
  }
}

/**
 * Validate and normalize a retained firmware descriptor. Unknown fields are
 * deliberately retained so a newer firmware can add metadata without making
 * an older Designer discard it.
 */
export function validateGpioDescriptor(input) {
  const diagnostics = []
  let parsed = input
  if (typeof input === 'string') {
    try {
      parsed = JSON.parse(input)
    } catch (error) {
      return {
        valid: false,
        descriptor: null,
        errors: [diagnostic('error', 'invalid-json', '$', `GPIO descriptor JSON could not be parsed: ${error.message}`)],
        warnings: [],
        diagnostics,
      }
    }
  }
  if (!isPlainObject(parsed)) {
    diagnostics.push(diagnostic('error', 'descriptor-not-object', '$', 'GPIO descriptor must be a JSON object.'))
    return { valid: false, descriptor: null, errors: diagnostics, warnings: [], diagnostics }
  }
  if (parsed.version !== undefined && parsed.version !== GPIO_METADATA_VERSION) {
    diagnostics.push(diagnostic('warning', 'unsupported-descriptor-version', 'version', `Unsupported GPIO descriptor version '${parsed.version}'.`))
  }
  if (parsed.descriptor_version !== undefined && parsed.descriptor_version !== GPIO_METADATA_VERSION) {
    diagnostics.push(diagnostic('warning', 'unsupported-descriptor-version', 'descriptor_version', `Unsupported GPIO descriptor version '${parsed.descriptor_version}'.`))
  }
  if (!validUInt32(parsed.config_revision)) {
    diagnostics.push(diagnostic('error', 'invalid-config-revision', 'config_revision', 'config_revision must be an unsigned 32-bit integer.'))
  }
  if (!Array.isArray(parsed.entities)) {
    diagnostics.push(diagnostic('error', 'entities-not-array', 'entities', 'GPIO descriptor entities must be an array.'))
  } else {
    if (parsed.entities.length > GPIO_MAX_BINDINGS) {
      diagnostics.push(diagnostic('error', 'too-many-entities', 'entities', `Firmware supports at most ${GPIO_MAX_BINDINGS} GPIO entities.`))
    }
    const ids = new Set()
    parsed.entities.forEach((entity, index) => {
      validateEntity(entity, index, diagnostics)
      if (isPlainObject(entity) && validEntityId(entity.id)) {
        if (ids.has(entity.id)) diagnostics.push(diagnostic('error', 'duplicate-entity-id', `entities[${index}].id`, `Entity ID '${entity.id}' is duplicated.`))
        ids.add(entity.id)
      }
    })
  }

  const descriptor = clone(parsed)
  if (Array.isArray(descriptor.entities)) {
    descriptor.entities = descriptor.entities.map(entity => {
      if (!isPlainObject(entity)) return entity
      return {
        ...entity,
        name: entity.name ?? entity.id ?? '',
        capabilities: entity.capabilities ?? [],
        properties: entity.properties ?? {},
      }
    })
  }
  const errors = diagnostics.filter(item => item.severity === 'error')
  const warnings = diagnostics.filter(item => item.severity === 'warning')
  return { valid: errors.length === 0, descriptor: errors.length === 0 ? descriptor : null, errors, warnings, diagnostics }
}

function emptyStatus(plateId, topic) {
  return {
    plateId,
    topic,
    available: false,
    online: false,
    cached: false,
    configRevision: null,
    receivedAt: null,
    entityCount: 0,
    error: null,
    status: 'unknown',
  }
}

/**
 * The descriptor the Designer works with, by priority: the live MQTT
 * descriptor of a connected plate (the device's truth), the GPIO configuration
 * stored with the plate (loaded from or deployed to the device, or edited
 * locally), and finally the last cached MQTT descriptor.
 */
function effectiveDescriptor(entry, plate, online) {
  if (online && entry?.descriptor) return { descriptor: entry.descriptor, source: 'device' }
  const fromPlate = gpioDescriptorFromConfig(isPlainObject(plate) ? plate.gpio_config : null)
  if (fromPlate) return { descriptor: fromPlate, source: 'plate' }
  return entry?.descriptor ? { descriptor: entry.descriptor, source: 'cache' } : { descriptor: null, source: null }
}

function publicEntry(entry, plate, online) {
  const { descriptor, source } = effectiveDescriptor(entry, plate, online)
  const status = entry?.status ?? emptyStatus(stablePlateId(plate), topicForPlate(plate))
  return {
    plateId: status.plateId,
    topic: plateName(plate) ? topicForPlate(plate) : status.topic,
    descriptor: clone(descriptor),
    entities: clone(descriptor?.entities ?? []),
    status: {
      ...status,
      source,
      online: Boolean(online),
      cached: source === 'cache',
      available: Boolean(descriptor),
      configRevision: entry?.configRevision ?? status.configRevision ?? null,
      receivedAt: entry?.receivedAt ?? status.receivedAt ?? null,
      entityCount: descriptor?.entities?.length ?? 0,
      error: entry?.lastError ?? status.error ?? null,
      status: online ? 'online' : (descriptor ? 'offline' : 'unknown'),
    },
  }
}

export class GpioMetadataAdapter {
  constructor(settings = null, {
    service = mqttService,
    storage = asStorage(),
    now = () => Date.now(),
    listenerName = null,
  } = {}) {
    this.service = service
    this.storage = storage
    this.now = now
    this.listenerName = listenerName ?? `gpio_metadata_adapter_${++adapterSequence}`
    this.settings = settings
    this.currentPlate = settings?.plate ?? settings ?? null
    this.currentPlateKey = stablePlateId(this.currentPlate)
    this.currentPlateConfig = plateConfigFingerprint(this.currentPlate)
    this.entries = new Map()
    this.subscribers = new Map()
    this.statusSubscribers = new Set()
    this.disposed = false
    this.loadCache()
    this.registerTransportListeners()
  }

  setSettings(settings) {
    return this.setPlate(settings?.plate ?? settings ?? null, settings)
  }

  setPlate(plate, settings = this.settings) {
    const previousKey = this.currentPlateKey
    const nextKey = stablePlateId(plate)
    const previousConfig = this.currentPlateConfig
    this.settings = settings
    this.currentPlate = plate
    this.currentPlateKey = nextKey
    this.currentPlateConfig = plateConfigFingerprint(plate)
    if (previousKey !== nextKey) {
      this.emit(previousKey, { reason: 'plate-change' })
      this.emit(nextKey, { reason: 'plate-change' })
    } else if (previousConfig !== this.currentPlateConfig) {
      // The plate's stored GPIO configuration changed (loaded, deployed or saved).
      this.emit(nextKey, { reason: 'plate-config' })
    } else if (previousKey && this.entries.get(previousKey)?.topic !== topicForPlate(plate)) {
      // A rename changes the MQTT prefix. Keep the cache associated with the
      // same stable plate ID, but do not present it as live on the new topic.
      const entry = this.entries.get(nextKey)
      if (entry) entry.live = false
      this.emit(nextKey, { reason: 'plate-rename' })
    }
    return this
  }

  listEntities(plateId = this.currentPlate) {
    const plate = this.resolvePlate(plateId)
    const key = stablePlateId(plate)
    return clone(effectiveDescriptor(this.entries.get(key), plate, this.isPlateOnline(plate)).descriptor?.entities ?? [])
  }

  getEntity(plateId, entityId) {
    return this.listEntities(plateId).find(entity => entity.id === entityId) ?? null
  }

  getProperty(plateId, entityId, property) {
    return this.getEntity(plateId, entityId)?.properties?.[property] ?? null
  }

  getStatus(plateId = this.currentPlate) {
    const plate = this.resolvePlate(plateId)
    const key = stablePlateId(plate)
    const entry = this.entries.get(key)
    const online = this.isPlateOnline(plate)
    return clone(publicEntry(entry, plate, online).status)
  }

  subscribe(plateId, callback) {
    if (typeof plateId === 'function') {
      callback = plateId
      plateId = this.currentPlate
    }
    const plate = this.resolvePlate(plateId)
    const key = stablePlateId(plate)
    if (!this.subscribers.has(key)) this.subscribers.set(key, new Set())
    const callbacks = this.subscribers.get(key)
    callbacks.add(callback)
    return () => {
      callbacks.delete(callback)
      if (callbacks.size === 0) this.subscribers.delete(key)
    }
  }

  onStatus(callback) {
    if (typeof callback !== 'function') return () => {}
    this.statusSubscribers.add(callback)
    return () => this.statusSubscribers.delete(callback)
  }

  removePlate(plateId) {
    const plate = this.resolvePlate(plateId)
    const key = stablePlateId(plate)
    this.entries.delete(key)
    this.persistCache()
    this.emit(key, { reason: 'plate-removed' })
  }

  dispose() {
    if (this.disposed) return
    this.disposed = true
    this.unregisterTransportListeners()
    this.subscribers.clear()
    this.statusSubscribers.clear()
  }

  registerTransportListeners() {
    this.service?.registerListener?.(this.listenerName, (_timestamp, topic, payload) => {
      this.handleMessage(topic, payload)
    })
    this.service?.registerConnectionListener?.(this.listenerName, status => {
      this.handleConnectionStatus(status)
    })
  }

  unregisterTransportListeners() {
    this.service?.unregisterListener?.(this.listenerName)
    this.service?.unregisterConnectionListener?.(this.listenerName)
  }

  handleConnectionStatus(status) {
    const connected = status === true || status === 'connected' || status?.connected === true
    const disconnected = status === false || status === 'disconnected' || status?.connected === false
    if (!connected && !disconnected) return
    const key = this.currentPlateKey
    if (disconnected && key && this.entries.has(key)) this.entries.get(key).live = false
    if (key) this.emit(key, { reason: connected ? 'mqtt-connect' : 'mqtt-disconnect' })
    this.statusSubscribers.forEach(callback => callback({ connected, plateId: key }))
  }

  handleMessage(topic, payload) {
    if (this.disposed || typeof topic !== 'string') return false
    const plate = this.currentPlate
    const expectedTopic = topicForPlate(plate)
    // Exact matching is intentional: runtime state topics must never be
    // interpreted as metadata, and another plate must not populate this cache.
    if (!expectedTopic || topic !== expectedTopic) return false
    const result = validateGpioDescriptor(payload)
    if (!result.valid) {
      const key = this.currentPlateKey
      const entry = this.entries.get(key)
      if (entry) entry.lastError = result.errors[0]?.message ?? 'Invalid GPIO descriptor'
      this.emit(key, { reason: result.errors[0]?.code ?? 'invalid-descriptor', error: result.errors[0] })
      return false
    }
    this.storeDescriptor(plate, result.descriptor, result.warnings)
    return true
  }

  storeDescriptor(plate, descriptor, warnings = []) {
    const key = stablePlateId(plate)
    if (!key) return false
    const topic = topicForPlate(plate)
    const previous = this.entries.get(key)
    const changed = !previous || descriptorFingerprint(previous.descriptor) !== descriptorFingerprint(descriptor)
    const entry = {
      plateId: key,
      topic,
      descriptor: clone(descriptor),
      configRevision: descriptor.config_revision,
      receivedAt: changed ? this.now() : previous.receivedAt,
      lastError: null,
      warnings: clone(warnings),
      live: true,
    }
    this.entries.set(key, entry)
    const becameLive = !previous?.live
    if (changed) this.persistCache()
    if (changed || becameLive) {
      const reason = !previous
        ? 'descriptor-received'
        : (previous.configRevision !== descriptor.config_revision
            ? 'descriptor-revision'
            : (becameLive ? 'descriptor-received' : 'descriptor-change'))
      this.emit(key, { reason })
    }
    return changed || becameLive
  }

  resolvePlate(plateId) {
    if (plateId === undefined || plateId === null || plateId === '') return this.currentPlate
    if (isPlainObject(plateId)) return plateId
    if (String(plateId) === this.currentPlateKey) return this.currentPlate
    return { id: plateId, name: String(plateId) }
  }

  isPlateOnline(plate) {
    if (stablePlateId(plate) !== this.currentPlateKey) return false
    return Boolean(this.service?.isConnected?.() && this.entries.get(this.currentPlateKey)?.live)
  }

  emit(key, { reason = 'update', error = null } = {}) {
    if (!key || this.disposed) return
    const plate = key === this.currentPlateKey ? this.currentPlate : { id: key, name: this.entries.get(key)?.topic?.split('/')[1] ?? key }
    const entry = this.entries.get(key)
    if (entry && error) entry.lastError = error.message ?? String(error)
    const event = publicEntry(entry, plate, this.isPlateOnline(plate))
    event.reason = reason
    event.status.error = entry?.lastError ?? null
    this.subscribers.get(key)?.forEach(callback => callback(event))
    this.statusSubscribers.forEach(callback => callback(event))
  }

  loadCache() {
    if (!this.storage) return
    try {
      const raw = this.storage.getItem(GPIO_METADATA_CACHE_KEY)
      const cache = raw ? JSON.parse(raw) : {}
      if (!isPlainObject(cache)) return
      const plates = isPlainObject(cache.plates) ? cache.plates : cache
      for (const [key, entry] of Object.entries(plates)) {
        if (!isPlainObject(entry) || !isPlainObject(entry.descriptor)) continue
        const result = validateGpioDescriptor(entry.descriptor)
        if (!result.valid) continue
        this.entries.set(key, {
          plateId: key,
          topic: entry.topic ?? '',
          descriptor: result.descriptor,
          configRevision: result.descriptor.config_revision,
          receivedAt: Number.isFinite(entry.receivedAt) ? entry.receivedAt : null,
          lastError: null,
          warnings: [],
          live: false,
        })
      }
    } catch {
      // A corrupt cache behaves like a cache miss; live MQTT data remains usable.
    }
  }

  persistCache() {
    if (!this.storage) return
    const cache = Object.fromEntries([...this.entries.entries()].map(([key, entry]) => [key, {
      plateId: key,
      topic: entry.topic,
      descriptor: entry.descriptor,
      configRevision: entry.configRevision,
      receivedAt: entry.receivedAt,
    }]))
    try {
      this.storage.setItem(GPIO_METADATA_CACHE_KEY, JSON.stringify({ version: GPIO_METADATA_VERSION, plates: cache }))
    } catch {
      // Persistence is best effort; the in-memory cache remains authoritative.
    }
  }
}

export default GpioMetadataAdapter
